import { create } from 'zustand';

interface Variable {
  name: string;
  path: string;
  type: 'string' | 'number' | 'boolean' | 'date' | 'array' | 'object';
  sampleValue?: any;
}

interface Clause {
  id: string;
  name: string;
  category: string;
  jurisdictions: string[];
}

interface TemplateState {
  // Template data
  templateId: string | null;
  templateName: string;
  templateContent: string;
  templateCategory: string;
  templateJurisdictions: string[];
  
  // Editor state
  isDirty: boolean;
  isSaving: boolean;
  lastSaved: Date | null;
  
  // Preview
  previewData: Record<string, any>;
  previewContent: string;
  previewErrors: string[];
  
  // Variables & Clauses
  detectedVariables: Variable[];
  availableClauses: Clause[];
  
  // UI state
  activeTab: 'editor' | 'preview' | 'variables' | 'settings';
  sidebarOpen: boolean;
  sidebarTab: 'variables' | 'clauses' | 'filters' | 'help';
  
  // Actions
  setTemplateContent: (content: string) => void;
  setTemplateName: (name: string) => void;
  setTemplateCategory: (category: string) => void;
  setPreviewData: (data: Record<string, any>) => void;
  setPreviewContent: (content: string) => void;
  setPreviewErrors: (errors: string[]) => void;
  setDetectedVariables: (vars: Variable[]) => void;
  setActiveTab: (tab: 'editor' | 'preview' | 'variables' | 'settings') => void;
  setSidebarOpen: (open: boolean) => void;
  setSidebarTab: (tab: 'variables' | 'clauses' | 'filters' | 'help') => void;
  markDirty: () => void;
  markSaved: () => void;
  resetTemplate: () => void;
  loadTemplate: (template: {
    id: string;
    name: string;
    content: string;
    category: string;
    jurisdictions: string[];
  }) => void;
}

const defaultPreviewData: Record<string, any> = {
  grantor: {
    type: 'individual',
    firstName: 'John',
    middleName: 'William',
    lastName: 'Smith',
    maritalStatus: 'married',
    spouse: {
      firstName: 'Jane',
      middleName: 'Marie',
      lastName: 'Smith'
    }
  },
  grantee: {
    type: 'individual',
    firstName: 'Robert',
    middleName: 'James',
    lastName: 'Johnson',
    maritalStatus: 'single'
  },
  property: {
    address: {
      street1: '123 Main Street',
      city: 'Sturgeon Bay',
      state: 'WI',
      zipCode: '54235',
      county: 'Door'
    },
    legalDescription: 'Lot 5, Block 2, of SUNSET ADDITION to the City of Sturgeon Bay, Door County, Wisconsin, according to the recorded plat thereof.',
    parcelId: '012-0123-4567-000'
  },
  consideration: 350000,
  executionDate: new Date().toISOString(),
  preparedBy: {
    name: 'Sarah K. Johnson',
    barNumber: '1089234',
    firm: 'Lawity Legal'
  }
};

export const useTemplateStore = create<TemplateState>((set) => ({
  // Initial state
  templateId: null,
  templateName: 'Untitled Template',
  templateContent: '',
  templateCategory: 'real-estate',
  templateJurisdictions: ['WI'],
  
  isDirty: false,
  isSaving: false,
  lastSaved: null,
  
  previewData: defaultPreviewData,
  previewContent: '',
  previewErrors: [],
  
  detectedVariables: [],
  availableClauses: [],
  
  activeTab: 'editor',
  sidebarOpen: true,
  sidebarTab: 'variables',
  
  // Actions
  setTemplateContent: (content) => set({ templateContent: content, isDirty: true }),
  setTemplateName: (name) => set({ templateName: name, isDirty: true }),
  setTemplateCategory: (category) => set({ templateCategory: category, isDirty: true }),
  setPreviewData: (data) => set({ previewData: data }),
  setPreviewContent: (content) => set({ previewContent: content }),
  setPreviewErrors: (errors) => set({ previewErrors: errors }),
  setDetectedVariables: (vars) => set({ detectedVariables: vars }),
  setActiveTab: (tab) => set({ activeTab: tab }),
  setSidebarOpen: (open) => set({ sidebarOpen: open }),
  setSidebarTab: (tab) => set({ sidebarTab: tab }),
  markDirty: () => set({ isDirty: true }),
  markSaved: () => set({ isDirty: false, isSaving: false, lastSaved: new Date() }),
  resetTemplate: () => set({
    templateId: null,
    templateName: 'Untitled Template',
    templateContent: '',
    templateCategory: 'real-estate',
    templateJurisdictions: ['WI'],
    isDirty: false,
    previewContent: '',
    previewErrors: [],
  }),
  loadTemplate: (template) => set({
    templateId: template.id,
    templateName: template.name,
    templateContent: template.content,
    templateCategory: template.category,
    templateJurisdictions: template.jurisdictions,
    isDirty: false,
    lastSaved: new Date(),
  }),
}));

// Sample clauses for the sidebar
export const sampleClauses: Clause[] = [
  { id: 'warranty_covenant', name: 'Warranty Covenant', category: 'Real Estate', jurisdictions: ['WI', 'FL'] },
  { id: 'quitclaim_covenant', name: 'Quitclaim Covenant', category: 'Real Estate', jurisdictions: ['WI', 'FL'] },
  { id: 'homestead_waiver', name: 'Homestead Waiver', category: 'Real Estate', jurisdictions: ['FL'] },
  { id: 'marital_property_waiver', name: 'Marital Property Waiver', category: 'Real Estate', jurisdictions: ['WI'] },
  { id: 'notary_acknowledgment', name: 'Notary Acknowledgment', category: 'General', jurisdictions: ['WI', 'FL'] },
  { id: 'witness_attestation', name: 'Witness Attestation', category: 'General', jurisdictions: ['WI', 'FL'] },
  { id: 'self_proving_affidavit', name: 'Self-Proving Affidavit', category: 'Estate Planning', jurisdictions: ['WI', 'FL'] },
  { id: 'no_contest_clause', name: 'No Contest Clause', category: 'Estate Planning', jurisdictions: ['WI', 'FL'] },
  { id: 'llc_purpose', name: 'LLC Purpose Statement', category: 'Business', jurisdictions: ['WI', 'FL'] },
  { id: 'indemnification', name: 'Indemnification Clause', category: 'Business', jurisdictions: ['WI', 'FL'] },
];

// Built-in filters reference
export const builtInFilters = [
  { name: 'uppercase', description: 'Convert to uppercase', example: '{{name | uppercase}} → "JOHN SMITH"' },
  { name: 'lowercase', description: 'Convert to lowercase', example: '{{name | lowercase}} → "john smith"' },
  { name: 'capitalize', description: 'Capitalize first letter', example: '{{name | capitalize}} → "John smith"' },
  { name: 'titlecase', description: 'Title case all words', example: '{{name | titlecase}} → "John Smith"' },
  { name: 'currency', description: 'Format as USD currency', example: '{{amount | currency}} → "$350,000.00"' },
  { name: 'number', description: 'Format number with decimals', example: '{{value | number:"2"}} → "1234.56"' },
  { name: 'percent', description: 'Format as percentage', example: '{{rate | percent}} → "5.5%"' },
  { name: 'ordinal', description: 'Add ordinal suffix', example: '{{day | ordinal}} → "5th"' },
  { name: 'words', description: 'Number to words', example: '{{amount | words}} → "three hundred fifty thousand"' },
  { name: 'date', description: 'Format date (custom)', example: '{{date | date:"MMMM d, yyyy"}} → "January 5, 2025"' },
  { name: 'shortdate', description: 'Short date format', example: '{{date | shortdate}} → "01/05/2025"' },
  { name: 'longdate', description: 'Long date format', example: '{{date | longdate}} → "January 5, 2025"' },
  { name: 'legalname', description: 'Format legal name', example: '{{party | legalname}} → "John William Smith"' },
  { name: 'partydesignation', description: 'Party with role', example: '{{party | partydesignation:"Grantor"}} → "John Smith (hereinafter \\"Grantor\\")"' },
  { name: 'possessive', description: 'Add possessive form', example: '{{name | possessive}} → "Smith\'s"' },
  { name: 'default', description: 'Default if empty', example: '{{value | default:"N/A"}} → "N/A"' },
  { name: 'trim', description: 'Remove whitespace', example: '{{text | trim}}' },
  { name: 'replace', description: 'Replace text', example: '{{text | replace:"old":"new"}}' },
  { name: 'truncate', description: 'Truncate with ellipsis', example: '{{text | truncate:"50"}}' },
];

// DSL syntax help
export const syntaxHelp = [
  {
    category: 'Variables',
    items: [
      { syntax: '{{variable}}', description: 'Simple variable' },
      { syntax: '{{object.property}}', description: 'Nested property' },
      { syntax: '{{variable | filter}}', description: 'With filter' },
      { syntax: '{{variable | filter:"arg"}}', description: 'Filter with argument' },
    ]
  },
  {
    category: 'Conditionals',
    items: [
      { syntax: '{{#if condition}}...{{/if}}', description: 'Simple conditional' },
      { syntax: '{{#if condition}}...{{#else}}...{{/if}}', description: 'If-else' },
      { syntax: '{{#if a == "value"}}...{{/if}}', description: 'Equality check' },
      { syntax: '{{#if a AND b}}...{{/if}}', description: 'Logical AND' },
      { syntax: '{{#if a OR b}}...{{/if}}', description: 'Logical OR' },
      { syntax: '{{#if NOT a}}...{{/if}}', description: 'Negation' },
    ]
  },
  {
    category: 'Loops',
    items: [
      { syntax: '{{#each items as item}}...{{/each}}', description: 'Basic loop' },
      { syntax: '{{#each items as item, idx}}...{{/each}}', description: 'With index' },
      { syntax: '{{item.property}}', description: 'Access item property' },
      { syntax: '{{idx}}', description: 'Current index (1-based)' },
    ]
  },
  {
    category: 'Clauses',
    items: [
      { syntax: '{{@clause:id}}', description: 'Insert clause' },
      { syntax: '{{@clause:id:WI}}', description: 'Jurisdiction-specific' },
    ]
  },
  {
    category: 'Other',
    items: [
      { syntax: '{{!-- comment --}}', description: 'Comment (not rendered)' },
      { syntax: '{{= expression}}', description: 'Computed value' },
      { syntax: '{{= SUM(items.amount)}}', description: 'Sum array property' },
      { syntax: '{{= COUNT(items)}}', description: 'Count array items' },
    ]
  },
];
